// Send questionnaire as three steps, from the Assessments list (release 3.3,
// O1 of the 7 Oct plan, section 3.6): 1 Vendor and questionnaire, 2 Dates and
// reminders, 3 Review and send.
//
// # WHY THE RULES ARE HERE AND NOT IN THE SCREEN
//
// The steps add no rule of their own. The send is the same call the vendor's
// own send screen makes (POST /v1/vendors/{id}/invite), and every refusal it
// can give is said before the step that would run into it: the request step
// and the other due diligence gates (lib/requestRules.ts, the server's own
// rule mirrored), a suspended vendor's reason, a vendor nobody answers for,
// and the answer-by date (D11, lib/dateRules.ts). Which step a person may stand
// on is decided here as plain functions, so a test can walk them without a
// browser: a step whose earlier step still has a problem is not reachable,
// and an address that names it opens the first step with the problem.
//
// # THE ADDRESS
//
// #/assessments/send is a page of its own under the Assessments route, so
// App.tsx needs no new entry. The step is a query value (?step=dates), not a
// path, because the console remounts the page on every path change and the
// answers would go with it. Each step is pushed, so Back steps back with the
// answers kept (Back model B3, B5); the chosen vendor is in the address so a
// link opens the same send (B7). When the send is done, or cancelled, the
// steps it pushed are stepped back out of before the page moves on, so Back
// never reopens a finished send (B9).

import type { MessagingPolicy } from './api'
import { checkDueAfterToday } from './dateRules'
import { dueDiligenceRefusal, type VendorStanding } from './requestRules'

export const SEND_STEPS = [
  { key: 'vendor', label: 'Vendor and questionnaire' },
  { key: 'dates', label: 'Dates and reminders' },
  { key: 'review', label: 'Review and send' },
] as const

export type SendStep = (typeof SEND_STEPS)[number]['key']

export const SEND_PATH = '#/assessments/send'

// Whether an address is the send page (with or without a step or vendor).
export function isSendAddress(hash: string): boolean {
  return /^#\/assessments\/send\/?(\?|$)/.test(hash)
}

// The step an address names, as an index. Anything else is the first step.
export function stepIndex(raw: string | null | undefined): number {
  const at = SEND_STEPS.findIndex((s) => s.key === raw)
  return at < 0 ? 0 : at
}

// The address of a step, with the chosen vendor kept in it.
export function sendAddress(step: number, vendorId: string): string {
  const q = new URLSearchParams()
  if (vendorId) q.set('vendor', vendorId)
  const key = SEND_STEPS[step]?.key
  if (key && key !== 'vendor') q.set('step', key)
  const s = q.toString()
  return SEND_PATH + (s ? '?' + s : '')
}

export type SendAnswers = { vendorId: string; questionnaire: string; dueOn: string; reason: string }

// What the screen has read about the chosen vendor. vendor is null until it
// is read.
export type SendFacts = {
  vendor: VendorStanding | null
  recipient: boolean
  questionnaires: number
}

// What stops a step from being finished, in a sentence; empty when nothing
// does. The first step's problems are the server's refusals, in its order.
export function stepProblem(step: number, a: SendAnswers, f: SendFacts, today: string): string {
  if (step === 0) {
    if (!a.vendorId) return 'Choose a vendor.'
    if (!f.vendor) return 'Reading the vendor.'
    const gate = dueDiligenceRefusal(f.vendor, a.reason)
    if (gate) return gate.message
    if (f.questionnaires === 0) return 'No questionnaire is published yet.'
    if (!a.questionnaire) return 'Choose a questionnaire.'
    if (!f.recipient) return 'Add the person who answers for the vendor.'
    return ''
  }
  if (step === 1) return checkDueAfterToday('Answer-by date', a.dueOn, today)
  return ''
}

// The furthest step a person may stand on: the first one with a problem, or
// the last.
export function reachableStep(a: SendAnswers, f: SendFacts, today: string): number {
  for (let i = 0; i < SEND_STEPS.length - 1; i++) {
    if (stepProblem(i, a, f, today)) return i
  }
  return SEND_STEPS.length - 1
}

// The step drawn: the one the address names, unless an earlier step still
// has a problem, which is then the one drawn.
export function shownStep(asked: number, a: SendAnswers, f: SendFacts, today: string): number {
  return Math.min(Math.max(0, asked), reachableStep(a, f, today))
}

// Whether the step drawn takes the focus to its form: only when the reader
// moved to it. `was` is the step drawn when the page was last ready (null
// before it ever was), `ready` that the vendor's data has arrived (or no
// vendor is chosen). The step a reload lands on (?step=) is decided when the
// data arrives and what is drawn before that is a guess, so neither moves the
// focus: it moves once, on the reader's next step (O1 review nit, 13 Oct).
export function stepTakesFocus(was: number | null, step: number, ready: boolean): boolean {
  return ready && was !== null && was !== step
}

// ------------------------------------------------------------- reminders

const CHANNEL: Record<string, string> = { EMAIL: 'Email', WHATSAPP: 'WhatsApp', SMS: 'SMS' }

// The reminders the vendor will be sent, from the workspace's messaging
// policy (Settings, Vendor messaging). The send itself takes no reminder
// setting; a reader who may not read the policy is told how reminders work
// in general rather than a schedule that may not be this workspace's.
export function reminderWords(policy: Pick<MessagingPolicy, 'reminderLadder' | 'reminderDaysBeforeDue'> | null): string {
  if (!policy) return 'A reminder goes out as the due date nears and again once it passes.'
  const days = policy.reminderDaysBeforeDue
  const first =
    days > 0 ? 'First reminder ' + days + (days === 1 ? ' day' : ' days') + ' before the due date' : 'First reminder on the due date'
  const ladder = (policy.reminderLadder ?? []).map((c) => CHANNEL[c] ?? c)
  const tries =
    ladder.length > 1 ? ' Tries ' + ladder.join(', then ') + '.' : ladder.length === 1 ? ' By ' + ladder[0] + '.' : ''
  return first + ', a second the day after it.' + tries
}

// --------------------------------------------------------------- history

// The steps this page pushed are marked with how deep they are and with a
// token of this page load. After a reload the entries below belong to the
// page before, and stepping back into them would load the console again, so
// a mark from another load counts as none.
const LOAD = Math.random().toString(36).slice(2, 10)

export function stepDepth(state: unknown): number {
  const s = (state ?? {}) as { sendDepth?: unknown; sendLoad?: unknown }
  return s.sendLoad === LOAD && typeof s.sendDepth === 'number' && s.sendDepth > 0 ? s.sendDepth : 0
}

// The state a pushed step carries: the entry's own state (where the send was
// opened from) one step deeper, without the scroll key (lib/scrollPlace.ts),
// which belongs to the entry below.
export function pushedState(state: unknown): Record<string, unknown> {
  const { k: _k, ...kept } = (state ?? {}) as Record<string, unknown>
  return { ...kept, sendDepth: stepDepth(state) + 1, sendLoad: LOAD }
}

// Steps back out of the entries this page pushed, so what comes next replaces
// the entry the send was opened on and Back from there never lands on a step
// of a finished send. Resolves once the browser has moved, or after two
// seconds if it raises nothing (it would have left the page).
export function stepOut(depth: number): Promise<void> {
  if (depth <= 0 || typeof window === 'undefined') return Promise.resolve()
  return new Promise((done) => {
    const finish = () => {
      window.clearTimeout(guard)
      window.removeEventListener('popstate', finish)
      done()
    }
    const guard = window.setTimeout(finish, 2000)
    window.addEventListener('popstate', finish)
    history.go(-depth)
  })
}

// --------------------------------------------------------------- draft

// The answers, kept for this tab while the steps are walked, so a reload
// lands on the same step with the same answers. Keyed by vendor; dropped once
// the questionnaire is sent or the send is cancelled.
const DRAFT_KEY = 'tprm.send.'

export type SendDraft = { questionnaire: string; dueOn: string; reason: string }

export function readDraft(vendorId: string): SendDraft | null {
  if (!vendorId) return null
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY + vendorId)
    if (!raw) return null
    const d = JSON.parse(raw) as Partial<SendDraft>
    return { questionnaire: String(d.questionnaire ?? ''), dueOn: String(d.dueOn ?? ''), reason: String(d.reason ?? '') }
  } catch {
    return null
  }
}

export function writeDraft(vendorId: string, d: SendDraft): void {
  if (!vendorId) return
  try {
    sessionStorage.setItem(DRAFT_KEY + vendorId, JSON.stringify(d))
  } catch {
    // A tab that keeps nothing still sends; a reload starts the steps again.
  }
}

export function dropDraft(vendorId: string): void {
  try {
    sessionStorage.removeItem(DRAFT_KEY + vendorId)
  } catch {
    // Nothing kept, nothing to drop.
  }
}
